import React, { useState, useEffect, useRef } from 'react';
import { getSessionHeaders } from '../lib/serverSession'

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface PinVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called once the user has verified the action (PIN or magic link).
   * Receives the non-secret action_verification_tokens.id (bigint as number)
   * that downstream MFA endpoints use to look up the verified record.
   */
  onVerified: (verificationId?: number) => void;
  userId: number;
  email: string;
  purpose: 'withdrawal' | 'security_change' | 'profile_update' | 'email_verification';
  title?: string;
  description?: string;
}

/**
 * Action Verification Modal — v4.6.3
 *
 * Primary path: user receives a 6-digit PIN via email and types it into the
 * input below. PIN is bcrypt-compared server-side and is immune to
 * email-scanner prefetch / SafeLinks token consumption.
 *
 * Fallback path: the email also contains a magic link. If the user clicks
 * it, the polling loop notices verified_at flip and resolves automatically.
 * The 256-bit secret token never returns to the browser in either path.
 *
 * Lifecycle contract (v4.6.3): on a successful verification we ONLY call
 * onVerified(verificationId). The parent is responsible for unmounting us
 * (by flipping its show* flag or transitioning its stage). Calling onClose
 * from the success path would clobber the parent's post-verify state
 * transition (see TotpMfaPanel.reset() bug).
 */
export const PinVerificationModal: React.FC<PinVerificationModalProps> = ({
  isOpen,
  onClose,
  onVerified,
  userId,
  email,
  purpose,
  title = 'Security Verification Required',
  description
}) => {
  const [sending, setSending] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [expired, setExpired] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [verificationId, setVerificationId] = useState<number | null>(null);
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pinInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-send verification link when modal opens
  useEffect(() => {
    if (isOpen && !linkSent && !sending) {
      sendVerificationLink();
    }
    return () => stopPolling();
  }, [isOpen]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = (pollVerificationId: number) => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/check-action-verification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ verificationId: pollVerificationId })
        });

        const result = await response.json();

        if (result.verified) {
          stopPolling();
          setSuccess(true);
          // Hand the verified id to the parent. The parent is responsible for
          // unmounting this modal (either by flipping its show* flag or by
          // transitioning to the next stage that gates this component). Calling
          // handleClose() here would also invoke onClose, which on some parents
          // resets the very state onVerified just set — race condition fixed
          // in v4.6.3.
          setTimeout(() => onVerified(pollVerificationId), 1000);
        } else if (result.expired) {
          stopPolling();
          setExpired(true);
          setError('Verification link has expired. Please request a new one.');
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000); // Poll every 3 seconds
  };

  const sendVerificationLink = async () => {
    setSending(true);
    setError(null);
    setExpired(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/send-action-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, email, purpose })
      });

      const result = await response.json();

      if (result.success && result.verificationId != null) {
        const vid = Number(result.verificationId);
        if (!Number.isFinite(vid) || vid <= 0) {
          setError('Invalid verification id received from server.');
          return;
        }
        setLinkSent(true);
        setVerificationId(vid);
        setCountdown(60);
        startPolling(vid);
      } else {
        setError(result.error || 'Failed to send verification link');
      }
    } catch (err) {
      console.error('Error sending verification link:', err);
      setError('Failed to send verification link');
    } finally {
      setSending(false);
    }
  };

  const submitPin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (verifying || success) return;
    if (verificationId == null) {
      setError('Please request a code first.');
      return;
    }
    if (!/^\d{6}$/.test(pin)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/verify-action-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ verificationId, pin })
      });
      const result = await response.json();
      if (result.success && result.verified) {
        stopPolling();
        setSuccess(true);
        // See note in startPolling(): do NOT call handleClose() here. The
        // parent owns the modal's lifecycle and will unmount us on its own
        // state transition; calling onClose would clobber that transition.
        setTimeout(() => onVerified(verificationId), 800);
        return;
      }
      if (result.expired || response.status === 410) {
        setExpired(true);
        setError(result.error || 'Verification code has expired. Please request a new one.');
        return;
      }
      if (typeof result.attemptsRemaining === 'number') {
        setAttemptsRemaining(result.attemptsRemaining);
      }
      if (result.locked) {
        setExpired(true);
      }
      setError(result.error || 'Verification failed');
      setPin('');
    } catch (err) {
      console.error('Error verifying PIN:', err);
      setError('Network error. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleClose = () => {
    stopPolling();
    setError(null);
    setSuccess(false);
    setLinkSent(false);
    setExpired(false);
    setCountdown(0);
    setVerificationId(null);
    setPin('');
    setAttemptsRemaining(null);
    onClose();
  };

  // Auto-focus the PIN input once a code has been issued.
  useEffect(() => {
    if (linkSent && !success && !expired && pinInputRef.current) {
      pinInputRef.current.focus();
    }
  }, [linkSent, success, expired]);

  if (!isOpen) return null;

  const maskedEmail = email.replace(/(.{2}).*(@.*)/, '$1***$2');

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-2xl border border-slate-700/50 p-6 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{title}</h2>
          <p className="text-slate-400 text-sm">
            {description || (sending
              ? 'Sending verification code to your email...'
              : linkSent
                ? 'Enter the 6-digit code from your email below.'
                : 'We will email a 6-digit verification code to your address.'
            )}
          </p>
        </div>

        {/* Code Sent Status */}
        {linkSent && !success && !expired && (
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-4">
            <p className="text-blue-300 text-sm text-center">
              📧 Code sent to <strong>{maskedEmail}</strong>
            </p>
          </div>
        )}

        {/* PIN Entry */}
        {linkSent && !success && !expired && (
          <form onSubmit={submitPin} className="mb-4">
            <label htmlFor="action-verification-pin" className="block text-slate-300 text-sm mb-2 text-center">
              Verification code
            </label>
            <input
              id="action-verification-pin"
              ref={pinInputRef}
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={pin}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setPin(v);
                if (error) setError(null);
              }}
              disabled={verifying || success}
              className="w-full bg-slate-900 border border-slate-600 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30 outline-none text-white text-center text-2xl tracking-[0.6em] font-mono py-3 px-4 rounded-lg disabled:opacity-50"
              placeholder="••••••"
              aria-label="6-digit verification code"
            />
            {typeof attemptsRemaining === 'number' && attemptsRemaining > 0 && attemptsRemaining < 5 && !error && (
              <p className="text-amber-400 text-xs text-center mt-2">
                {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining
              </p>
            )}
          </form>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p className="text-green-300 text-sm text-center">
              ✅ Verified successfully!
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 mb-4">
            <p className="text-red-300 text-sm text-center">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {linkSent && !expired && (
            <button
              type="button"
              onClick={() => submitPin()}
              disabled={verifying || success || pin.length !== 6}
              className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-slate-900 font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? 'Verifying...' : 'Verify'}
            </button>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={sendVerificationLink}
              disabled={sending || (countdown > 0 && !expired) || success}
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending...' : countdown > 0 && !expired ? `Resend (${countdown}s)` : expired ? 'Send New Code' : linkSent ? 'Resend Code' : 'Send Code'}
            </button>

            <button
              type="button"
              onClick={handleClose}
              disabled={success}
              className="flex-1 bg-gray-600 hover:bg-gray-500 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-4 text-center">
          <p className="text-slate-500 text-xs">
            Code expires in 10 minutes. Check your spam folder if you don't see the email.
          </p>
        </div>
      </div>
    </div>
  );
};
